import React, { useState } from "react";
import type { BillingDocument, LineItem } from "../../types";
import { money, totals, uid } from "../../lib/utils";
import { useBilling } from "../../store/BillingContext";

export const DocumentModal: React.FC = () => {
  const { activeDoc, setActiveDoc, saveDocument, state } = useBilling();

  if (!activeDoc) return null;

  const [form, setForm] = useState<BillingDocument>(() => structuredClone(activeDoc));

  const updateForm = (patch: Partial<BillingDocument>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const handleClientChange = (clientId: string) => {
    const selected = state.clients.find((c) => c.id === clientId);
    if (selected) {
      updateForm({
        clientId: selected.id,
        company: selected.company || selected.name,
        email: selected.email,
        phone: selected.phone,
        address: selected.address,
        clientGstin: selected.gstin,
      });
    } else {
      updateForm({ clientId: "" });
    }
  };

  const updateLineItem = (itemId: string, patch: Partial<LineItem>) => {
    const nextItems = form.items.map((item) => {
      if (item.id !== itemId) return item;
      const updated = { ...item, ...patch };
      const qty = Number(updated.qty) || 0;
      const rate = Number(updated.rate) || 0;
      const disc = Number(updated.disc) || 0;
      updated.amount = qty * rate * (1 - disc / 100);
      return updated;
    });
    updateForm({ items: nextItems });
  };

  const addLineItem = () => {
    const newItem: LineItem = {
      id: uid(),
      desc: "",
      hsn: "998361",
      qty: 1,
      unit: "Month",
      rate: 0,
      disc: 0,
      amount: 0,
    };
    updateForm({ items: [...form.items, newItem] });
  };

  const addFromService = (serviceId: string) => {
    if (!serviceId) return;
    const s = state.services.find((x) => x.id === serviceId);
    if (!s) return;
    const newItem: LineItem = {
      id: uid(),
      desc: s.name,
      hsn: s.hsn || "998361",
      qty: 1,
      unit: s.unit || "Month",
      rate: s.rate || 0,
      disc: 0,
      amount: s.rate || 0,
      serviceId: s.id,
    };
    updateForm({ items: [...form.items, newItem] });
  };

  const removeLineItem = (itemId: string) => {
    if (form.items.length === 1) {
      // Keep at least one item
      updateLineItem(itemId, { desc: "", qty: 1, rate: 0, disc: 0, amount: 0 });
      return;
    }
    updateForm({ items: form.items.filter((i) => i.id !== itemId) });
  };

  // Recalculate totals in real time
  const currentTotals = totals(form.items, form.gstRate);

  const isInvoice = form.type === "invoice";
  const title = `${form.id ? "Edit" : "New"} ${isInvoice ? "Invoice" : "Quotation"}`;

  return (
    <div className="overlay">
      <div className="modal document">
        <div className="modal-head">
          <h2>{title} ({form.number})</h2>
          <button onClick={() => setActiveDoc(null)}>×</button>
        </div>

        <div className="modal-body">
          <div className="form-grid">
            <label>
              <span>Document Number</span>
              <input
                type="text"
                value={form.number}
                onChange={(e) => updateForm({ number: e.target.value })}
                required
              />
            </label>

            <label>
              <span>{isInvoice ? "Invoice Date" : "Quotation Date"}</span>
              <input
                type="date"
                value={form.date}
                onChange={(e) => updateForm({ date: e.target.value })}
                required
              />
            </label>

            <label>
              <span>{isInvoice ? "Due Date" : "Valid Until"}</span>
              <input
                type="date"
                value={form.due}
                onChange={(e) => updateForm({ due: e.target.value })}
                required
              />
            </label>

            <label>
              <span>Quick Select Client</span>
              <select
                value={form.clientId || ""}
                onChange={(e) => handleClientChange(e.target.value)}
              >
                <option value="">— Or select existing client —</option>
                {state.clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company || c.name} {c.email ? `(${c.email})` : ""}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Client / Company Name</span>
              <input
                type="text"
                value={form.company}
                placeholder="e.g. Acme Corp"
                onChange={(e) => updateForm({ company: e.target.value })}
                required
              />
            </label>

            <label>
              <span>Client Email</span>
              <input
                type="email"
                value={form.email}
                placeholder="billing@client.com"
                onChange={(e) => updateForm({ email: e.target.value })}
              />
            </label>

            <label>
              <span>Phone</span>
              <input
                type="text"
                value={form.phone}
                placeholder="+91 98765 43210"
                onChange={(e) => updateForm({ phone: e.target.value })}
              />
            </label>

            <label>
              <span>Client GSTIN</span>
              <input
                type="text"
                value={form.clientGstin}
                placeholder="29AAAAA0000A1Z5"
                onChange={(e) => updateForm({ clientGstin: e.target.value })}
              />
            </label>

            <label>
              <span>Place of Supply (POS)</span>
              <input
                type="text"
                value={form.pos}
                placeholder="Karnataka"
                onChange={(e) => updateForm({ pos: e.target.value })}
              />
            </label>

            <label>
              <span>Payment Mode</span>
              <input
                type="text"
                value={form.paymentMethod}
                placeholder="Bank Transfer / NEFT"
                onChange={(e) => updateForm({ paymentMethod: e.target.value })}
              />
            </label>

            <label className="wide">
              <span>Billing Address</span>
              <textarea
                value={form.address}
                rows={2}
                placeholder="Street address, city, state, pincode"
                onChange={(e) => updateForm({ address: e.target.value })}
              />
            </label>
          </div>

          <h4>Line Items & Services</h4>
          <div className="table-wrap">
            <table className="line-table">
              <thead>
                <tr>
                  <th style={{ minWidth: "240px" }}>Item Description</th>
                  <th style={{ width: "90px" }}>HSN/SAC</th>
                  <th style={{ width: "70px" }}>Qty</th>
                  <th style={{ width: "90px" }}>Unit</th>
                  <th style={{ width: "110px" }}>Rate (₹)</th>
                  <th style={{ width: "80px" }}>Disc %</th>
                  <th style={{ width: "120px", textAlign: "right" }}>Amount (₹)</th>
                  <th style={{ width: "40px" }} />
                </tr>
              </thead>
              <tbody>
                {form.items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <input
                        type="text"
                        placeholder="Description of service or deliverable"
                        value={item.desc}
                        onChange={(e) => updateLineItem(item.id, { desc: e.target.value })}
                        required
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        placeholder="998361"
                        value={item.hsn}
                        onChange={(e) => updateLineItem(item.id, { hsn: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="1"
                        step="any"
                        value={item.qty}
                        onChange={(e) => updateLineItem(item.id, { qty: Number(e.target.value) })}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={item.unit}
                        placeholder="Month"
                        onChange={(e) => updateLineItem(item.id, { unit: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={item.rate}
                        onChange={(e) => updateLineItem(item.id, { rate: Number(e.target.value) })}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={item.disc}
                        onChange={(e) => updateLineItem(item.id, { disc: Number(e.target.value) })}
                      />
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 700, paddingRight: "12px" }}>
                      {money(item.amount)}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        type="button"
                        onClick={() => removeLineItem(item.id)}
                        className="danger"
                        title="Remove Item"
                        style={{ border: "none", background: "transparent", cursor: "pointer" }}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="line-actions">
            <button type="button" onClick={addLineItem}>
              <i className="fa-solid fa-plus" /> Add Line
            </button>
            <select
              defaultValue=""
              onChange={(e) => {
                addFromService(e.target.value);
                e.currentTarget.value = "";
              }}
            >
              <option value="">+ Add service from catalog...</option>
              {state.services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} — {money(s.rate)} ({s.billing})
                </option>
              ))}
            </select>
          </div>

          <div className="totals">
            <div>
              <span>Subtotal:</span>
              <b>{money(currentTotals.subtotal)}</b>
            </div>
            {currentTotals.discTotal > 0 && (
              <div style={{ color: "#b91c1c" }}>
                <span>Total Discount:</span>
                <b>-{money(currentTotals.discTotal)}</b>
              </div>
            )}
            <div>
              <span>
                GST Rate (%):
                <input
                  type="number"
                  min="0"
                  max="28"
                  value={form.gstRate}
                  onChange={(e) => updateForm({ gstRate: Number(e.target.value) })}
                  style={{ width: "60px", marginLeft: "8px", padding: "4px" }}
                />
              </span>
              <b>{money(currentTotals.gstAmt)}</b>
            </div>
            <div className="grand">
              <span>Grand Total:</span>
              <b>{money(currentTotals.total)}</b>
            </div>
          </div>

          <label className="wide">
            <span>Notes, Payment Terms & Instructions</span>
            <textarea
              value={form.notes}
              rows={3}
              placeholder="Include bank reference, due period, and contact for queries."
              onChange={(e) => updateForm({ notes: e.target.value })}
            />
          </label>
        </div>

        <div className="modal-foot">
          <button type="button" onClick={() => setActiveDoc(null)}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              saveDocument(form, false);
            }}
          >
            Save Draft
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => {
              saveDocument(form, true);
            }}
          >
            <i className="fa-solid fa-paper-plane" /> Save & Send
          </button>
        </div>
      </div>
    </div>
  );
};
